import React, { ChangeEvent, useCallback, useState } from 'react';

import {
  Badge,
  Box,
  Button,
  Chip,
  Flex,
  FlexProps,
  Grid,
  Loader,
  Menu,
  Paper,
  Space,
  Text,
  TextInput,
  Tooltip,
  useMantineTheme
} from '@mantine/core';
import { IconChevronDown, IconPlus } from '@tabler/icons-react';
import { TagCreateModal } from 'components/TagComponents/TagCreateModal';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Resource, UnUploadedResource } from 'shared/interfaces/Resource';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { isUplodedResources } from 'shared/utils/resource';

const ITEMS_DEBOUNCE_THRESHOLD = 40;
const DEBOUNCE_TIME_MS = 150;

interface SearchBarProps extends FlexProps {
  filters: Set<string>;
  selectedFilters: Set<string>;
  placeholder: string;
  setSelectedFilters: (filters: Set<string>) => void;
  onSearchValueChange: (value: string) => void;
  onFilterCreate?: () => void;
  resources?: Resource[] | UnUploadedResource[];
  smartAssistants?: SmartAssistant[];
  rightSection?: React.ReactNode;
  dropdownStyle?: React.CSSProperties;
}

export function SearchBar({
  filters,
  onFilterCreate,
  selectedFilters,
  setSelectedFilters,
  onSearchValueChange,
  placeholder,
  resources,
  smartAssistants,
  rightSection,
  dropdownStyle,
  ...flexProps
}: SearchBarProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [createTagModalOpened, setCreateTagModalOpened] = useState(false);
  const [noTagsTooltipOpened, setNoTagsTooltipOpened] = useState(false);

  const debouncedOnChange = useCallback(
    debounce(
      (value: string) => {
        onSearchValueChange(value);
        setIsLoading(false);
      },
      (resources?.length ?? smartAssistants?.length ?? 0) < ITEMS_DEBOUNCE_THRESHOLD
        ? DEBOUNCE_TIME_MS
        : DEBOUNCE_TIME_MS / 2
    ),
    [onSearchValueChange]
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setSearchValue(value);
    setIsLoading(true);
    debouncedOnChange(value);
  };

  const onTagClick = (tag: string) => {
    if (selectedFilters.has(tag)) {
      selectedFilters.delete(tag);
      setSelectedFilters(new Set(selectedFilters));
    } else {
      selectedFilters.add(tag);
      setSelectedFilters(new Set(selectedFilters));
    }
  };

  return (
    <>
      <Flex direction="row" align="center" w="100%" {...flexProps}>
        <Paper withBorder w="100%" h="100%" sx={{ boxShadow: theme.other.baseShadow }}>
          <Flex align="center" w="100%" h="100%">
            <Menu withinPortal={false} shadow="md" width="100%">
              <Menu.Target>
                <Tooltip position="bottom" label={t('SEARCH_BAR.NO_TAGS_NOTICE')} opened={noTagsTooltipOpened}>
                  <Box
                    sx={{}}
                    onMouseEnter={() => filters.size <= 0 && setNoTagsTooltipOpened(true)}
                    onMouseLeave={() => setNoTagsTooltipOpened(false)}>
                    <Button
                      variant="light"
                      sx={{
                        backgroundColor: theme.other.gray.bg,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0
                      }}
                      rightIcon={<IconChevronDown color={theme.other.text.default} />}>
                      {selectedFilters.size > 0 && (
                        <>
                          <Badge px={4} size="xs">
                            {selectedFilters.size}
                          </Badge>
                          <Space w={10} />
                        </>
                      )}
                      <Text color={theme.other.text.default}>{t('TAG.TITLE')}</Text>
                    </Button>
                  </Box>
                </Tooltip>
              </Menu.Target>

              <Menu.Dropdown style={dropdownStyle} w={dropdownStyle?.width}>
                <Grid p="xs" gutter="xs">
                  {Array.from(filters).map((tag: string, key: number) => {
                    return (
                      <Grid.Col span="content" key={key}>
                        <Chip size="xs" checked={selectedFilters.has(tag)} onChange={() => onTagClick(tag)}>
                          {tag}
                        </Chip>
                      </Grid.Col>
                    );
                  })}
                </Grid>
                {onFilterCreate && (
                  <>
                    <Menu.Divider />
                    <Menu.Item key="all" icon={<IconPlus />} p="xs" onClick={() => setCreateTagModalOpened(true)}>
                      <Text>{`${t('APP.ACTIONS.CREATE')}`}</Text>
                    </Menu.Item>
                  </>
                )}
              </Menu.Dropdown>
            </Menu>
            <TextInput
              pl="xs"
              w="100%"
              placeholder={placeholder}
              variant="unstyled"
              value={searchValue}
              onChange={handleChange}
              rightSection={isLoading ? <Loader size={18} /> : null}
            />
          </Flex>
        </Paper>

        {rightSection && (
          <>
            <Space w={10} />
            {rightSection}
          </>
        )}
      </Flex>
      {(resources || smartAssistants) && (
        <TagCreateModal
          opened={createTagModalOpened}
          onClose={() => setCreateTagModalOpened(false)}
          resources={isUplodedResources(resources) ? resources : undefined}
          smartAssistants={smartAssistants}
          onTagCreated={onFilterCreate}
        />
      )}
    </>
  );
}
